import { useEffect, useRef } from "react";
import {
  CORE_LIVING_CORE_V2,
  CORE_V2_BASE_FRAGMENT,
  CORE_V2_VERTEX,
  createCoreV2Effects,
} from "./coreLivingCoreV2Spec";

type UniformName = "uResolution" | "uTime" | "uMouse" | "uImpulse" | "uImage";

type RenderLayer = {
  readonly program: WebGLProgram;
  readonly buffer: WebGLBuffer;
  readonly attribute: number;
  readonly components: number;
  readonly count: number;
  readonly mode: number;
  readonly uniforms: Readonly<Record<UniformName, WebGLUniformLocation | null>>;
};

type CoreRenderer = {
  resize: (width: number, height: number, pixelRatio: number) => void;
  render: (timeSeconds: number, mouseX: number, mouseY: number) => void;
  destroy: () => void;
};

const UNIFORM_NAMES: readonly UniformName[] = ["uResolution", "uTime", "uMouse", "uImpulse", "uImage"];

function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(maximum, Math.max(minimum, value));
}

function compileShader(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("CORE Living Core V2: shader allocation failed");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || "unknown shader compilation error";
    gl.deleteShader(shader);
    throw new Error(`CORE Living Core V2 shader compilation failed: ${message}`);
  }
  return shader;
}

function createLayer(
  gl: WebGLRenderingContext,
  vertex: string,
  fragment: string,
  data: Float32Array,
  attributeName: string,
  components: number,
  mode: number,
): RenderLayer {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertex);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragment);
  const program = gl.createProgram();
  if (!program) {
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    throw new Error("CORE Living Core V2: program allocation failed");
  }

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || "unknown program linking error";
    gl.deleteProgram(program);
    throw new Error(`CORE Living Core V2 shader linking failed: ${message}`);
  }

  const buffer = gl.createBuffer();
  if (!buffer) {
    gl.deleteProgram(program);
    throw new Error("CORE Living Core V2: buffer allocation failed");
  }
  gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
  gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);

  const attribute = gl.getAttribLocation(program, attributeName);
  if (attribute < 0) {
    gl.deleteBuffer(buffer);
    gl.deleteProgram(program);
    throw new Error(`CORE Living Core V2: missing attribute ${attributeName}`);
  }

  const uniforms = Object.fromEntries(
    UNIFORM_NAMES.map((name) => [name, gl.getUniformLocation(program, name)]),
  ) as Record<UniformName, WebGLUniformLocation | null>;

  return {
    program,
    buffer,
    attribute,
    components,
    count: data.length / components,
    mode,
    uniforms,
  };
}

function createCoreRenderer(gl: WebGLRenderingContext, image: HTMLImageElement): CoreRenderer {
  const texture = gl.createTexture();
  if (!texture) throw new Error("CORE Living Core V2: texture allocation failed");

  const layers: RenderLayer[] = [];
  try {
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);

    layers.push(
      createLayer(
        gl,
        CORE_V2_VERTEX,
        CORE_V2_BASE_FRAGMENT,
        new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
        "aPosition",
        2,
        gl.TRIANGLES,
      ),
    );
    for (const effect of createCoreV2Effects()) {
      layers.push(
        createLayer(
          gl,
          effect.vertex,
          effect.fragment,
          effect.data,
          effect.attribute,
          effect.components,
          effect.mode === "points" ? gl.POINTS : gl.TRIANGLES,
        ),
      );
    }
  } catch (error) {
    for (const layer of layers) {
      gl.deleteBuffer(layer.buffer);
      gl.deleteProgram(layer.program);
    }
    gl.deleteTexture(texture);
    throw error;
  }

  let width = 1;
  let height = 1;
  let disposed = false;

  return {
    resize(nextWidth, nextHeight, pixelRatio) {
      if (disposed) return;
      width = Math.max(1, Math.round(nextWidth * pixelRatio));
      height = Math.max(1, Math.round(nextHeight * pixelRatio));
      gl.viewport(0, 0, width, height);
    },
    render(timeSeconds, mouseX, mouseY) {
      if (disposed) return;
      gl.viewport(0, 0, width, height);
      gl.clearColor(3 / 255, 5 / 255, 10 / 255, 1);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.disable(gl.BLEND);

      layers.forEach((layer, index) => {
        if (index === 1) {
          gl.enable(gl.BLEND);
          gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
        }
        gl.useProgram(layer.program);
        gl.bindBuffer(gl.ARRAY_BUFFER, layer.buffer);
        gl.enableVertexAttribArray(layer.attribute);
        gl.vertexAttribPointer(layer.attribute, layer.components, gl.FLOAT, false, 0, 0);

        const { uniforms } = layer;
        if (uniforms.uResolution) gl.uniform2f(uniforms.uResolution, width, height);
        if (uniforms.uTime) gl.uniform1f(uniforms.uTime, timeSeconds);
        if (uniforms.uMouse) gl.uniform2f(uniforms.uMouse, mouseX, mouseY);
        if (uniforms.uImpulse) gl.uniform1f(uniforms.uImpulse, 0);
        if (uniforms.uImage) gl.uniform1i(uniforms.uImage, 0);
        gl.drawArrays(layer.mode, 0, layer.count);
      });
      gl.disable(gl.BLEND);
    },
    destroy() {
      if (disposed) return;
      disposed = true;
      for (const layer of layers) {
        gl.deleteBuffer(layer.buffer);
        gl.deleteProgram(layer.program);
      }
      gl.deleteTexture(texture);
    },
  };
}

function supportsMatchMedia() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}

/**
 * The private CORE login background only. It never owns focus or pointer input:
 * login/OAuth remains native HTML above it. The fallback is the approved
 * sphere-only source mask, so WebGL failures never expose rasterized UI.
 */
export function CoreLivingBackground() {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const initiallyReduced = supportsMatchMedia() && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas || typeof window === "undefined") return;

    let destroyed = false;
    let contextLost = false;
    let frame = 0;
    let elapsedSeconds = 0;
    let lastFrameAt = 0;
    let mouseX = 0;
    let mouseY = 0;
    let renderer: CoreRenderer | null = null;
    let imageReady = false;
    let reducedMotion = supportsMatchMedia() && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let pageVisible = typeof document === "undefined" || !document.hidden;

    const media = supportsMatchMedia() ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      powerPreference: "high-performance",
      preserveDrawingBuffer: false,
    });

    const setFallback = () => {
      root.dataset.renderer = "fallback";
      root.dataset.webglRendered = "false";
    };
    const setWebgl = () => {
      root.dataset.renderer = "webgl";
      root.dataset.webglRendered = "true";
    };
    const isActive = () => Boolean(renderer && imageReady && !destroyed && !contextLost && !reducedMotion && pageVisible);
    const setMotionState = () => {
      root.dataset.motion = isActive() ? "running" : "paused";
    };
    const stop = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      lastFrameAt = 0;
    };
    const draw = () => renderer?.render(elapsedSeconds, mouseX, mouseY);
    const tick = (now: number) => {
      frame = 0;
      if (!isActive()) {
        setMotionState();
        return;
      }
      if (lastFrameAt === 0) lastFrameAt = now;
      const deltaSeconds = Math.min(0.1, Math.max(0, now - lastFrameAt) / 1000);
      elapsedSeconds += deltaSeconds;
      lastFrameAt = now;
      draw();
      root.dataset.webglRendered = "true";
      frame = window.requestAnimationFrame(tick);
    };
    const start = () => {
      if (!isActive() || frame) {
        setMotionState();
        return;
      }
      setMotionState();
      frame = window.requestAnimationFrame(tick);
    };
    const syncActivity = () => {
      stop();
      if (renderer && imageReady) draw();
      if (isActive()) start();
      else setMotionState();
    };

    const resize = () => {
      if (!renderer) return;
      const bounds = root.getBoundingClientRect();
      const cssWidth = Math.max(1, Math.round(bounds.width));
      const cssHeight = Math.max(1, Math.round(bounds.height));
      const nativeDpr = Math.max(1, window.devicePixelRatio || 1);
      const pixelBudgetRatio = Math.sqrt(CORE_LIVING_CORE_V2.maxDevicePixels / (cssWidth * cssHeight));
      const dpr = Math.min(CORE_LIVING_CORE_V2.maxDevicePixelRatio, nativeDpr, pixelBudgetRatio);
      canvas.width = Math.max(1, Math.round(cssWidth * dpr));
      canvas.height = Math.max(1, Math.round(cssHeight * dpr));
      renderer.resize(cssWidth, cssHeight, dpr);
      draw();
    };

    const onVisibilityChange = () => {
      pageVisible = !document.hidden;
      syncActivity();
    };
    const onReducedMotionChange = () => {
      reducedMotion = media?.matches ?? false;
      syncActivity();
    };
    const onPointerMove = (event: PointerEvent) => {
      const bounds = root.getBoundingClientRect();
      const within = event.clientX >= bounds.left && event.clientX <= bounds.right
        && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      if (!within) {
        mouseX = 0;
        mouseY = 0;
        return;
      }
      mouseX = clamp(2 * ((event.clientX - bounds.left) / Math.max(bounds.width, 1) - 0.5), -1, 1);
      mouseY = clamp(2 * ((event.clientY - bounds.top) / Math.max(bounds.height, 1) - 0.3), -1, 1);
    };
    const onContextLost = (event: Event) => {
      event.preventDefault();
      contextLost = true;
      stop();
      renderer?.destroy();
      renderer = null;
      setFallback();
      setMotionState();
    };
    const onContextRestored = () => {
      // A restored context deliberately remains on the approved static mask.
      // This avoids a second async allocation and honours graceful fallback.
      contextLost = true;
      setFallback();
      setMotionState();
    };

    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    resizeObserver?.observe(root);
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    document.addEventListener("visibilitychange", onVisibilityChange);
    canvas.addEventListener("webglcontextlost", onContextLost);
    canvas.addEventListener("webglcontextrestored", onContextRestored);
    if (media) {
      if (typeof media.addEventListener === "function") media.addEventListener("change", onReducedMotionChange);
      else (media as unknown as { addListener?: (listener: () => void) => void }).addListener?.(onReducedMotionChange);
    }

    if (!gl) {
      setFallback();
      setMotionState();
      return () => {
        resizeObserver?.disconnect();
        window.removeEventListener("resize", resize);
        window.removeEventListener("pointermove", onPointerMove);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        canvas.removeEventListener("webglcontextlost", onContextLost);
        canvas.removeEventListener("webglcontextrestored", onContextRestored);
        if (media) {
          if (typeof media.removeEventListener === "function") media.removeEventListener("change", onReducedMotionChange);
          else (media as unknown as { removeListener?: (listener: () => void) => void }).removeListener?.(onReducedMotionChange);
        }
      };
    }

    const source = new Image();
    source.decoding = "async";
    source.onload = () => {
      if (destroyed || contextLost) return;
      try {
        renderer = createCoreRenderer(gl, source);
        imageReady = true;
        resize();
        setWebgl();
        syncActivity();
      } catch {
        renderer?.destroy();
        renderer = null;
        imageReady = false;
        setFallback();
        setMotionState();
      }
    };
    source.onerror = () => {
      imageReady = false;
      setFallback();
      setMotionState();
    };
    source.src = CORE_LIVING_CORE_V2.sourcePath;
    setFallback();
    setMotionState();

    return () => {
      destroyed = true;
      stop();
      resizeObserver?.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      canvas.removeEventListener("webglcontextrestored", onContextRestored);
      if (media) {
        if (typeof media.removeEventListener === "function") media.removeEventListener("change", onReducedMotionChange);
        else (media as unknown as { removeListener?: (listener: () => void) => void }).removeListener?.(onReducedMotionChange);
      }
      renderer?.destroy();
      renderer = null;
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className="core-living-background"
      aria-hidden="true"
      data-testid="core-living-background"
      data-renderer="fallback"
      data-webgl-rendered="false"
      data-motion={initiallyReduced ? "paused" : "running"}
      data-ribbons={CORE_LIVING_CORE_V2.ribbonCount}
      data-particles={CORE_LIVING_CORE_V2.particleCount}
    >
      <img
        className="core-living-fallback"
        src={CORE_LIVING_CORE_V2.sourcePath}
        alt=""
        aria-hidden="true"
      />
      <canvas ref={canvasRef} className="core-living-canvas" />
    </div>
  );
}
